import { ComponentType, IMultiSelect } from '@common/types/element';
import React, { FC, Fragment } from 'react';
import {
  ActivityIndicator,
  ScrollView,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { TouchableRipple, useTheme } from 'react-native-paper';
import createStyles from './style';
import useMultipleCheckbox, {
  SELECT_ALL,
  useMultipleCheckboxProps,
} from './hook';
import Checkbox from '@common/components/Checkbox';

const MultipleCheckbox: FC<IMultiSelect> = (attributes) => {
  const {
    value,
    list,
    handleChangeValue,
    hasLoadMore,
    isLoading,
    isAllSelected,
    selectedCount,
    clearAll,
    onLoadMore,
  }: useMultipleCheckboxProps = useMultipleCheckbox(attributes);

  const theme = useTheme();
  const styles = createStyles(attributes);
  attributes = {
    ...attributes,
    ...theme,
    ...{
      activeColor: theme.colors.primary,
      inactiveColor: theme.colors.background,
    },
  };

  const {
    maxSelections,
    selectAllEnabled = false,
    showSelectCount = false,
  } = attributes;

  if (list.length <= 0) return <Fragment />;

  return (
    <View style={[styles.container, { width: attributes.width }]}>
      {/* Header with count and clear button */}
      {(showSelectCount || selectedCount > 0) && (
        <View style={styles.headerContainer}>
          {showSelectCount && (
            <Text style={styles.selectedCountText}>
              {selectedCount} selected
              {maxSelections ? ` of ${maxSelections}` : ''}
            </Text>
          )}
          {showSelectCount && selectedCount > 0 && (
            <TouchableOpacity onPress={clearAll} style={styles.clearButton}>
              <Text style={styles.clearButtonText}>Clear All</Text>
            </TouchableOpacity>
          )}
        </View>
      )}

      <ScrollView showsVerticalScrollIndicator={false}>
        {selectAllEnabled && list.length > 1 && (
          <TouchableRipple
            onPress={() => handleChangeValue(SELECT_ALL)}
            style={styles.selectAllItem}
          >
            <View style={styles.selectAllItem}>
              <Checkbox
                id={`${attributes.id}_select_all`}
                activeColor={theme.colors.primary}
                // inactiveColor={theme.colors.outline}
                dependencies={{
                  valueFormInputs: {
                    [`${attributes.id}_select_all`]: isAllSelected,
                  },
                }}
                type={ComponentType.checkbox}
                inactiveIcon={''}
              />
              <Text style={styles.selectAllText}>Select All</Text>
            </View>
          </TouchableRipple>
        )}

        {/* Checkbox Items */}
        {list.map(
          (
            item: { value: string | number; custom: any; label: any },
            index: React.Key | null | undefined
          ) => {
            const isSelected = value.includes(item.value);
            const isDisabled =
              maxSelections && !isSelected && value.length >= maxSelections;
            const checkboxId = `${attributes.id}_${item.value}`;

            return (
              <TouchableRipple
                key={index}
                onPress={() => !isDisabled && handleChangeValue(item.value)}
                style={{ opacity: isDisabled ? 0.5 : 1 }}
              >
                <View pointerEvents="none">
                  <View style={styles.checkboxItem}>
                    <Checkbox
                      {...attributes}
                      dependencies={{
                        valueFormInputs: {
                          [checkboxId]: isSelected,
                        },
                      }}
                      id={checkboxId}
                      type={ComponentType.checkbox}
                      inactiveIcon={''}
                    />
                    <Text
                      numberOfLines={attributes.numberOfLines || 0}
                      style={styles.checkboxText}
                    >
                      {item.custom || item.label}
                    </Text>
                  </View>
                </View>
              </TouchableRipple>
            );
          }
        )}

        {/* Max selections info */}
        {maxSelections && (
          <Text style={styles.maxSelectionsText}>
            Maximum {maxSelections} selections allowed
          </Text>
        )}

        {/* Load More Button */}
        {!isLoading && hasLoadMore && (
          <View style={styles.loadMoreContainer}>
            <TouchableOpacity onPress={onLoadMore}>
              <Text>More</Text>
            </TouchableOpacity>
          </View>
        )}
        {/* Load More Indicator */}
        {isLoading && (
          <View style={styles.loadMoreContainer}>
            <ActivityIndicator color={theme.colors.primary} />
          </View>
        )}
      </ScrollView>
    </View>
  );
};

export default MultipleCheckbox;
